/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React from 'react';
import autobind from 'autobind-decorator';
import Debounce from 'debounce-decorator';
import jmespath from 'jmespath';
import PropTypes from 'prop-types'

import {BaseComponent} from '../BaseComponent';

import {GuidUtils} from '../../Utils/GuidUtils';
import {ObjectUtils} from '../../Utils/ObjectUtils';

import {PermissionCheckerEnhance} from "./Enhancements/PermissionCheckerEnhance";
import {Conditional} from "../Conditional";

import * as renderers from './ModelRenderers';

export class GenericModelViewerOldBase extends BaseComponent {
    constructor(props) {
        super(props);

        this.setInitialState({
            validation: {},
            childValidation: {},
            editorGuid: GuidUtils.Generate()
        });

        this.validateTimeout = null;
    }

    componentDidMount() {
        super.componentDidMount();

        //set default values for FieldDefs.
        let groups = this.getFieldDefs(true);
        let updates = [];

        groups.forEach((group) => { // for each group
            group.value.forEach((fieldDef) => { // for each filed in it.
                if(fieldDef.defaultValue) { // if there is a default value
                    let value = fieldDef.getter(this.props.Data); //get the current

                    if(value == null) { // if it's blank
                        updates.push({ // set to the default.
                            def: fieldDef,
                            value: fieldDef.defaultValue
                        });
                    }
                }
            });
        });

        if(updates.length) {
            this.updateFields(updates);
        }

        this.validate();
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        if(typeof this.props.OnValidationUpdate == 'function') {
            this.props.OnValidationUpdate({}, this.state.editorGuid);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps, prevState);

        if(this.props.Data != prevProps.Data) {
            this.validate();
        }
    }

    @autobind
    fieldIsVisible(field) {
        const { EditMode, HasPermission, Data, RootData, CustomData } = this.props;

        // if this field is a child of an association
        // it should be hidden if the association is hidden
        if(field.association && !this.fieldIsVisible(field.association)) {
            return false;
        }

        // hide based on viewerOnly and editorOnly rules
        if((EditMode ? field.viewerOnly : field.editorOnly)) {
            return false;
        }

        // hide nullable associations {
        if(this.isHiddenNullableAssociationField(field)) {
            return false;
        }

        let isHidden = false;

        if(typeof field.isHidden == 'object') {
            const { query, requiresPermission } = field.isHidden;
            const SearchContext = ObjectUtils.Merge(ObjectUtils.Clone(Data), { __RootData: RootData, __CustomData: CustomData, editMode: EditMode });

            if(query) {
                isHidden = isHidden || jmespath.search(SearchContext, query);
            }
            if(requiresPermission) {
                isHidden = isHidden || !HasPermission(requiresPermission);
            }
        } else {
            isHidden = isHidden || field.isHidden;
        }

        return !isHidden;
    }

    @autobind
    fieldDefGroupIsVisible(group) {
        const { HideGroups } = this.props;
        if(HideGroups) {
            return !(HideGroups.includes(group.key));
        }
        return true;
    }

    @autobind
    isHiddenNullableAssociationField(fieldDef) {
        const { Data } = this.props;
        const association = fieldDef.association;

        if(association && association.name && !fieldDef.isNullableAssociationFieldToggle) {
            // if data is null and the association is nullable
            // (note: !isOne && ArrayOfChildren relationships
            // are not nullable)
            return Data[association.name] == null && (
                    (
                        association.type == 'ParentPointer' &&
                        association.isNullable
                    ) || (
                        association.type == "ArrayOfChildren" &&
                        association.isOne &&
                        association.isNullable
                )
            );
        }
        return false;
    }

    @autobind
    visibleFieldDefsForFieldDefGroup(group) {
        let fieldDefs = group.value;

        return {...group, value: fieldDefs.filter(this.fieldIsVisible)}
    }

    @autobind
    getFieldDefs(includeHidden = false) {
        const { Model, HideGroups } = this.props;
        let fieldDefGroups = Model.getFieldDefGroups();

        if(includeHidden) {
            return fieldDefGroups;
        }

        let visibleFieldDefGroups = fieldDefGroups
            .filter(this.fieldDefGroupIsVisible) // filters hidden groups
            .map(this.visibleFieldDefsForFieldDefGroup); // filters hidden fields within each group

        return visibleFieldDefGroups;
    }

    @autobind
    getKey() {
        return this.props.Model.getPrimaryKeyValue(this.props.Data);
    }

    @autobind
    updateFields(values) {
        if(typeof this.props.Update == 'function') {
            let target = ObjectUtils.Clone(this.props.Data);

            values.forEach((x) => {
                const resolvedValue = typeof x.value === 'function'
                    ? x.value(x.def.getter(target))
                    : x.value;
                x.def.setter(target, resolvedValue);
            });

            this.props.Update(target);
        }
    }

    @autobind
    updateField(def, value) {
        if(typeof this.props.Update == 'function') {
            let target = ObjectUtils.Clone(this.props.Data);
            const resolvedValue = typeof value === 'function'
                ? value(def.getter(target))
                : value;
            def.setter(target, resolvedValue);
            this.props.Update(target);
        }
    }

    @Debounce(300)
    async validate() {
        let ret = {};

        this.getFieldDefs().forEach((fieldGroup) =>
            fieldGroup.value.forEach((field) => {
                if(this.props.Association) {
                    if(this.props.Association.using) {
                        if(field.key === this.props.Association.using) {
                            return;
                        }
                    }
                }

                ret[field.key] = {
                    valid: true,
                    className: "",
                    message: ""
                };

                if(field.validators && field.validators.length) {
                    field.validators.forEach((validator) => {
                        const value = field.getter(this.props.Data);
                        let model = this.props.Model;
                        let data = this.props.Data;
                        const customData = this.props.CustomData;

                        // send appropriate model and data to child model validators
                        if(field.associationName && field.model) {
                            model = field.model;
                            data = data[field.associationName]
                        }

                        if (validator.show(this.props.EditMode)) {
                            if (!validator.logic(value, model, data, customData)) {
                                ret[field.key] = {
                                    id: field.key,
                                    valid: false,
                                    className: validator?.className(model) ?? 'sb-has-validation-message',
                                    message: validator?.message(model)
                                }
                            }
                        }
                    });
                }
            })
        );

        if(this.props.Model.recordValidators) {
            for (const validator of this.props.Model.recordValidators) {
                if (validator.show(this.props.EditMode)) {
                    const valid = await validator.logic(this.props.Model, this.props.Data);
                    if(!valid) {
                        ret[null] = {
                            id: GuidUtils.Generate(),
                            valid: false,
                            message: validator.message(this.props.Model, this.props.Data)
                        };
                    }
                }
            }
        }

        this.setState({ validation: ret }, () => {
            if(typeof this.props.OnValidationUpdate == 'function') {
                this.props.OnValidationUpdate(
                    {...this.state.validation, ...this.state.childValidation},
                    this.state.editorGuid,
                );
            }
        });
    }

    @autobind
    onChildValidation(childValidation, editorGuid) {
        let isValid = true;

        for (let key in childValidation) {
            if(!childValidation[key].valid) {
                isValid = false;
                break;
            }
        }

        if(!this.state.childValidation[editorGuid] || isValid != this.state.childValidation[editorGuid].valid) {
            let ret = ObjectUtils.Clone(this.state.childValidation);
            ret[editorGuid] = { valid: isValid };

            this.setState({ childValidation: ret }, () => {
                this.validate();
            });
        }
    }

    @autobind
    getOptionsForGroup(fieldGroup) {
        return {
            renderer: this.props.Model.groupRenderers?.[fieldGroup.key] ?? 'StandardModelViewerRenderer'
        }
    }

    @autobind
    renderFieldGroup(fieldGroup) {
        let groupOptions = this.getOptionsForGroup(fieldGroup);
        const RendererComponent = renderers?.[groupOptions.renderer];

        return   <Conditional key={fieldGroup.key} Condition={(fieldGroup.value ?? []).length > 0}>
                    <Conditional Condition={fieldGroup.key && fieldGroup.groupTitle !== '' && fieldGroup.groupTitle != null} key="header">
                        <h3 className="table-headers">{fieldGroup.groupTitle}</h3>
                    </Conditional>
                    <RendererComponent
                        key="body"
                        FieldGroup={fieldGroup}
                        SkipBorder={this.props.SkipBorder}
                        EditMode={this.props.EditMode}
                        RootData={this.props.RootData || this.props.Data}
                        Data={this.props.Data}
                        Validation={this.state.validation}
                        UpdateFields={this.updateFields}
                        UpdateField={this.updateField}
                        GetFieldDefs={this.getFieldDefs}
                        OnValidationUpdate={this.onChildValidation}
                        CustomData={this.props.CustomData}
                        IsNewModel={this.props.IsNewModel}
                        DefaultSelectedFieldDefKey={this.props.DefaultSelectedFieldDefKey}
                    />
                </Conditional>;
    }

    render() {
        const { Model, Data } = this.props;

        let classNames = []

        if(typeof Model?.classNamesFromData == "function"){
            classNames = classNames.concat(Model?.classNamesFromData(Data) ?? []);
        }

        return   <div className={classNames.join(' ')} onSubmit={e => e.preventDefault()}>
                        <Conditional Condition={this.state.validation[null]}>
                            <div className="sb-model-viewer-validation-failure-text">
                                {this.state.validation[null]?.message}
                            </div>
                        </Conditional>
                        {
                            this.getFieldDefs().map((fieldGroup) => {
                                return this.renderFieldGroup(fieldGroup)
                            })
                        }
                </div>;
    }
}

@PermissionCheckerEnhance
export class GenericModelViewerOld extends GenericModelViewerOldBase {}



GenericModelViewerOld.propTypes = {
  Association: PropTypes.object,
  CustomData: PropTypes.object,
  Data: PropTypes.object,
  DefaultSelectedFieldDefKey: PropTypes.string,
  EditMode: PropTypes.bool,
  HasPermission: PropTypes.any,
  HideGroups: PropTypes.any,
  IsNewModel: PropTypes.bool,
  Model: PropTypes.any.isRequired,
  OnValidationUpdate: PropTypes.func,
  RootData: PropTypes.object,
  SkipBorder: PropTypes.bool,
  Update: PropTypes.func
}

GenericModelViewerOld.defaultProps = {
  EditMode: false,
  IsNewModel: false,
  SkipBorder: false
}
